import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Lock, Info } from "lucide-react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import PermissionMatrix from "./PermissionMatrix";
import { useToast } from "../../context/ToastContext";
import { useCreateRole, useUpdateRole } from "../../api/users/useRolesApi";
import styles from "./RoleModal.module.css";

const emptyForm = {
  name: "",
  description: "",
  status: "ACTIVE",
  hierarchyLevel: "",
};

const RoleModal = ({ open, onClose, role, readOnly = false, permissions = [], onSaved }) => {
  const { showToast } = useToast();
  const { execute: createRole, loading: creating } = useCreateRole();
  const { execute: updateRole, loading: updating } = useUpdateRole();
  const saving = creating || updating;

  const isEdit = !!role;

  const [form, setForm] = useState(emptyForm);
  const [permIds, setPermIds] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (role) {
      setForm({
        name: role.name || "",
        description: role.description || "",
        status: role.status || "ACTIVE",
        hierarchyLevel: role.hierarchyLevel ?? "",
      });
      setPermIds((role.Permissions || []).map((p) => Number(p.id)));
    } else {
      setForm(emptyForm);
      setPermIds([]);
    }
    setError("");
  }, [open, role]);

  const setField = (k, v) => {
    setError("");
    setForm((f) => ({ ...f, [k]: v }));
  };

  const title = useMemo(() => {
    if (readOnly) return "View Role";
    return isEdit ? "Edit Role" : "Create Role";
  }, [readOnly, isEdit]);

  const handleSubmit = async () => {
    if (readOnly) return;
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Role name must be at least 2 characters");
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      status: form.status,
      permissions: permIds,
    };
    if (form.hierarchyLevel !== "" && form.hierarchyLevel != null) {
      payload.hierarchyLevel = Number(form.hierarchyLevel);
    }
    try {
      if (isEdit) {
        await updateRole(role.id, payload);
        showToast("Role updated successfully", "success");
      } else {
        await createRole(payload);
        showToast("Role created successfully", "success");
      }
      onSaved?.();
    } catch (err) {
      const msg =
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.message ||
        "Failed to save role";
      setError(msg);
      showToast(msg, "error");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      width="920px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Role"}
            </button>
          )}
        </>
      }
    >
      <div className={styles.body}>
        {readOnly && (
          <div className={styles.lockBanner}>
            <Lock size={15} />
            This is a system role. Its configuration is locked and shown for reference
            only.
          </div>
        )}

        {error && (
          <div className={styles.errorBanner}>
            <Info size={15} />
            {error}
          </div>
        )}

        <div className={styles.fields}>
          <div className={styles.field}>
            <label className={styles.label}>
              Role Name {!readOnly && <span className={styles.req}>*</span>}
            </label>
            <div className={styles.inputWrap}>
              <ShieldCheck size={15} className={styles.inputIcon} />
              <input
                className={styles.input}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. operations_manager"
                disabled={readOnly}
              />
            </div>
            <span className={styles.hint}>Lowercase, unique identifier</span>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Hierarchy Level</label>
            <input
              type="number"
              min={1}
              max={99}
              className={styles.input}
              value={form.hierarchyLevel}
              onChange={(e) => setField("hierarchyLevel", e.target.value)}
              placeholder="e.g. 3"
              disabled={readOnly}
            />
            <span className={styles.hint}>Lower = more privileged (1 highest)</span>
          </div>

          <div className={styles.fieldFull}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="What this role is for…"
              disabled={readOnly}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Status</label>
            <div className={styles.segmented}>
              {["ACTIVE", "INACTIVE"].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.segBtn} ${form.status === s ? styles.segActive : ""}`}
                  onClick={() => setField("status", s)}
                  disabled={readOnly}
                >
                  {s.charAt(0) + s.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.matrixSection}>
          <div className={styles.matrixHeader}>
            <h4 className={styles.matrixTitle}>Permissions</h4>
            <span className={styles.matrixCount}>{permIds.length} selected</span>
          </div>
          <PermissionMatrix
            permissions={permissions}
            value={permIds}
            onChange={setPermIds}
            readOnly={readOnly}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default RoleModal;
